import { Suspense, lazy, type ReactNode } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'

import { AuthProvider } from '@/features/auth/AuthProvider'
import { useAuth } from '@/features/auth/authContext'
import { useApplyBrand } from '@/hooks/useApplyBrand'
import { useApplyTheme } from '@/hooks/useApplyTheme'
import { env } from '@/lib/env'
import { queryClient } from '@/lib/query/queryClient'
import { ToastProvider } from '@/components/ui/Toast'
import { StompProvider } from '@/lib/ws/StompProvider'

const ReactQueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then((m) => ({ default: m.ReactQueryDevtools })),
)

/**
 * Inside the provider, so the socket opens and closes with the session. The
 * handshake carries the session cookie by itself — there is no token to pass.
 *
 * The backend has no `/ws` endpoint yet, so this stays off until it does:
 * a socket with nothing behind it reconnects every four seconds forever.
 */
function AuthedProviders({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()

  return (
    <StompProvider enabled={isAuthenticated && env.realtimeEnabled}>
      <ToastProvider>{children}</ToastProvider>
    </StompProvider>
  )
}

export function AppProviders({ children }: { children: ReactNode }) {
  // Order matters: the theme colour reads the palette step the brand hook writes.
  useApplyBrand()
  useApplyTheme()

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthedProviders>{children}</AuthedProviders>
      </AuthProvider>

      {env.isDev && (
        <Suspense fallback={null}>
          <ReactQueryDevtools buttonPosition="bottom-left" />
        </Suspense>
      )}
    </QueryClientProvider>
  )
}
